import { Backdrop, Box, CircularProgress, IconButton, InputAdornment, SelectChangeEvent, TextField, Theme, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { RootStateType } from "../../../../redux-store/reducers";
import { isModuleAllowed } from "../../../../utils/utilityFunctions";
import { useEffect, useRef, useState } from "react";
import { FamilyMembersFeeListDataObj, FamilyObj, GetFamilyMembersFeeListDataResponseBody, GetReportFamiliesListResponseBody, GetSecurityExclusionAuditDataResponseBody, GetSecurityExclusionDetailsResponseBody, SecurityExclusionAuditDataObj } from "../../../../redux-store/types/onBoarding";
import * as Yup from 'yup';
import { getFiscalYearOptions, getQuartersForFiscalYear } from "../../financialYear";
import { Form, Formik, FormikProps } from "formik";
import { getFamilyMembersFeeListData, getReportFamiliesList, getSecurityExclusionAuditData, getSecurityExclusionDetails } from "../../../../redux-store/actions/reports";
import { showError } from "../../../../redux-store/actions/auth";
import BreadCrum from "../../../../lib/BreadCrum";
import MFSelectField from "../../../../lib/formik/SelectField";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import { MFSubmitButton } from "../../../../lib/formik";
import { PaginationObj } from "../../../investorOverview/types";
import AuditDetailsPopup from "../../AuditDetailsPopup";
import AuditHistoryTable from "./AuditHistoryTable";
import NoData from "../../NoData";
import { SearchIcon } from "../../../customSVGs";
import { DialogCloseIcon } from "../../../../assets/SvgIcons";

interface ModifiedFamilyObj extends FamilyObj {
    label: string;
    value: string;
}

interface ModifiedFamilyMembersFeeListDataObj extends FamilyMembersFeeListDataObj {
    label: string;
    value: string;
}

interface FilterValues {
    year: string;
    quarter: string;
    family: ModifiedFamilyObj | null;
    individualArr: Array<ModifiedFamilyMembersFeeListDataObj>;
    individual: ModifiedFamilyMembersFeeListDataObj | null;
}

const initialFilterValues: FilterValues = {
    year: "",
    quarter: "",
    family: null,
    individualArr: [],
    individual: null
}

const filterValidationSchema = Yup.object().shape({
    year: Yup
        .string()
        .required('Please select Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
    family: Yup
        .object()
        .nullable()
        .test(
            "family-required",
            "Please select Family",
            function (value) { return !!value }
        ),
    individual: Yup
        .object()
        .nullable()
        .test(
            "individual-required",
            "Please select Individual",
            function (value) { return !!value }
        ),
})

const YEARS = getFiscalYearOptions()

const FILTER_FIELD_HEIGHT = "56px";
const FILTER_ERROR_GAP = "6px";
const FILTER_ERROR_SLOT_HEIGHT = "22px";
const FILTER_COLUMN_HEIGHT = `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP} + ${FILTER_ERROR_SLOT_HEIGHT})`;

const filterInputHeightSx = {
    "& .MuiInputBase-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
        padding: "15px",
        height: "100%",
        minHeight: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        padding: "0 9px",
        alignItems: "center",
        boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-input": {
        padding: "15px 6px",
    },
};

const filterErrorSx = {
    position: "absolute",
    left: 0,
    top: `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP})`,
    margin: 0,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
};

const filterFieldWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 1 auto" },
    minWidth: { xs: "100%", lg: "145px" },
    maxWidth: { xs: "100%", lg: "205px" },
    width: { xs: "100%", lg: "auto" },
    position: "relative",
    minHeight: FILTER_COLUMN_HEIGHT,
    "& .MuiFormControl-root": {
        my: 0,
        paddingBottom: 0,
    },
    "& .MuiFormHelperText-root": filterErrorSx,
    ...filterInputHeightSx,
};

const filterQuarterWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "240px" },
    maxWidth: { xs: "100%", lg: "280px" },
};

const filterSearchDropdownWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "300px" },
    maxWidth: { xs: "100%", lg: "350px" },
};

const filterSubmitWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 0 auto" },
    position: "relative",
    minHeight: { xs: "auto", lg: FILTER_COLUMN_HEIGHT },
    display: "flex",
    alignItems: "flex-start",
    width: { xs: "100%", lg: "auto" },
    marginTop: { xs: "1rem", lg: 0 }
};

const filterSubmitButtonSx = (theme: Theme) => ({
    ...theme.typography.body_500,
    fontWeight: 600,
    height: { xs: "auto", lg: FILTER_FIELD_HEIGHT },
    minHeight: { xs: "42px", lg: FILTER_FIELD_HEIGHT },
    maxHeight: { xs: "48px", lg: FILTER_FIELD_HEIGHT },
    py: { xs: "6px", sm: "8px", lg: 0 },
    px: { xs: "16px", sm: "20px", lg: "20px" },
    lineHeight: 1.2,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    width: { xs: "100%", lg: "auto" },
    minWidth: { xs: "unset", lg: "fit-content" },
    flexShrink: 0,
    "& .MuiCircularProgress-root": {
        color: theme.palette.text.primary,
    },
});

const DEBOUNCE_DELAY = 300

export default function AuditHistory(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);

    // State
    const [loadingStatus, setLoadingStatus] = useState<
        "tableDataLoading" |
        "appliedFilterLoading" |
        "individualDataLoading" |
        ""
    >("")
    const [familiesList, setFamiliesList] = useState<Array<ModifiedFamilyObj>>([])
    const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("")
    const [tableData, setTableData] = useState<Array<SecurityExclusionAuditDataObj>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
    })
    const [loadingRowId, setLoadingRowId] = useState<number | null>(null)
    const [auditDetailsPopupData, setAuditDetailsPopupData] = useState<{
        data: GetSecurityExclusionDetailsResponseBody | null;
        isOpen: boolean;
    }>({
        data: null,
        isOpen: false
    })

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'Security Exclusion Management',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        },
        {
            name: 'Audit History',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            isTextLowerCase: false
        }
    ] : [])

    const filterFormikRef = useRef<FormikProps<FilterValues>>(null);
    const lastAppliedFilterValuesRef = useRef<FilterValues | null>(null);

    // Ref that tracks whether the user has interacted with the search input. Prevents the debounced search useEffect from firing on initial mount or when `areFiltersApplied` first becomes true (which would cause a duplicate API call)
    const isSearchDirtyRef = useRef<boolean>(false);

    const handleYearChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            year: event.target.value as string,
            quarter: "",
            family: null,
            individualArr: [],
            individual: null
        })
    }

    const handleQuarterChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            quarter: event.target.value as string,
            family: null,
            individualArr: [],
            individual: null
        })
    }

    const fetchFamiliesData = async (searchInput: string) => {
        try {
            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    rmId: `${iwappRmId}`,
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportFamiliesList(payload)) as unknown as Promise<GetReportFamiliesListResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setFamiliesList([])

                    return
                }

                if (response?.isSuccess) {
                    const modifiedFamiliesList = (response?.families ?? [])
                        .filter((obj: FamilyObj) => `${obj?.family_name ?? ""}`.toLowerCase() !== "all")
                        .map((obj: FamilyObj) => ({
                            ...obj,
                            label: `${obj?.family_name ?? ""} ${obj?.family_id ? `(${obj.family_id})` : ''}`,
                            value: obj?.family_id
                        }))

                    setFamiliesList(modifiedFamiliesList)
                }
            } else {
                setFamiliesList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleFamilyChange = async (selectedObj: Record<string, any>) => {
        try {
            if (!filterFormikRef?.current) {
                return
            }

            const { values, setValues } = filterFormikRef.current

            if (!selectedObj) {
                setValues({
                    ...values,
                    family: null,
                    individualArr: [],
                    individual: null
                })

                return
            }

            setValues({
                ...values,
                family: selectedObj as ModifiedFamilyObj,
                individualArr: [],
                individual: null
            })

            setLoadingStatus("individualDataLoading")

            const payload = {
                year: Number(values.year),
                quarter: values.quarter,
                familyId: selectedObj?.family_id ?? ""
            }

            const response = await (dispatch(getFamilyMembersFeeListData(payload)) as unknown as Promise<GetFamilyMembersFeeListDataResponseBody>)

            if (!response) {
                return
            }

            if (!response?.isSuccess) {
                dispatch(showError(response?.message || "Data not found"))
                return
            }

            if (response.isSuccess) {
                const modifiedIndividualArr = (response?.data?.members ?? []).map((obj: FamilyMembersFeeListDataObj) => ({
                    ...obj,
                    label: `${obj?.name ?? ""} ${obj?.clientCode ? "(" + obj?.clientCode + ")" : ""} ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isHof ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""}`,
                    value: obj?.clientCode,
                }))

                setValues({
                    ...values,
                    family: selectedObj as ModifiedFamilyObj,
                    individualArr: modifiedIndividualArr ?? [],
                    individual: null
                })
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingStatus("")
        }
    }

    const fetchSecurityExclusionAuditData = async ({
        selectedYear,
        selectedQuarter,
        selectedFamilyId,
        selectedCurrentPage,
        selectedRowsPerPage,
        selectedClientCode,
        searchInput = "",
        cancelledRef
    }: {
        selectedYear: string;
        selectedQuarter: string;
        selectedFamilyId: string;
        selectedCurrentPage: number;
        selectedRowsPerPage: number;
        selectedClientCode: string;
        searchInput?: string;
        cancelledRef?: { current: boolean };
    }): Promise<boolean> => {

        const payload = {
            year: Number(selectedYear ?? ""),
            quarter: selectedQuarter,
            familyId: selectedFamilyId,
            currentPage: selectedCurrentPage,
            rowsPerPage: selectedRowsPerPage,
            clientCode: selectedClientCode,
            searchInput
        }

        const response = await (dispatch(getSecurityExclusionAuditData(payload)) as unknown as Promise<GetSecurityExclusionAuditDataResponseBody>)

        if (cancelledRef?.current) {
            return false
        }

        if (!response) {
            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return false
        }

        if (response) {
            setTableData(response?.data ?? [])
            setTablePaginationObj(prevState => ({
                ...prevState,
                currentPage: selectedCurrentPage - 1,
                rowsPerPage: selectedRowsPerPage,
                totalRows: response?.count ?? 0
            }))
        }

        return true
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            await fetchSecurityExclusionAuditData({
                selectedYear: lastAppliedFilterValuesRef.current?.year ?? "",
                selectedQuarter: lastAppliedFilterValuesRef.current?.quarter ?? "",
                selectedFamilyId: lastAppliedFilterValuesRef.current?.family?.family_id ?? "",
                selectedCurrentPage: 1,
                selectedRowsPerPage: Number(event.target.value),
                selectedClientCode: lastAppliedFilterValuesRef.current?.individual?.clientCode ?? "",
                searchInput: searchText
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) {
                return
            }

            setLoadingStatus("tableDataLoading")

            await fetchSecurityExclusionAuditData({
                selectedYear: lastAppliedFilterValuesRef.current?.year ?? "",
                selectedQuarter: lastAppliedFilterValuesRef.current?.quarter ?? "",
                selectedFamilyId: lastAppliedFilterValuesRef.current?.family?.family_id ?? "",
                selectedCurrentPage: page + 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                selectedClientCode: lastAppliedFilterValuesRef.current?.individual?.clientCode ?? "",
                searchInput: searchText
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleApplyFilters = async (values: FilterValues) => {
        try {
            isSearchDirtyRef.current = false

            setSearchText("")
            setLoadingStatus("appliedFilterLoading")

            const isSuccess = await fetchSecurityExclusionAuditData({
                selectedYear: values?.year ?? "",
                selectedQuarter: values?.quarter ?? "",
                selectedFamilyId: values?.family?.family_id ?? "",
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                selectedClientCode: values?.individual?.clientCode ?? "",
                searchInput: ""
            })

            if (isSuccess) {
                lastAppliedFilterValuesRef.current = { ...values }

                setAreFiltersApplied(true)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleViewDetailsClick = async (id: number) => {
        try {
            setLoadingRowId(id)

            const response = await (dispatch(getSecurityExclusionDetails(id)) as unknown as Promise<GetSecurityExclusionDetailsResponseBody>)

            if (response) {
                setAuditDetailsPopupData({
                    data: response ?? null,
                    isOpen: true
                })
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingRowId(null)
        }
    }

    const handleAuditDetailsPopupClose = () => {
        setAuditDetailsPopupData(prevState => ({
            ...prevState,
            isOpen: false
        }))
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        isSearchDirtyRef.current = true
        e.preventDefault()
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        isSearchDirtyRef.current = true
    }

    useEffect(() => {
        if (!areFiltersApplied) {
            return
        }

        if (!isSearchDirtyRef.current) {
            return
        }

        let cancelled = false
        const cancelledRef = { current: false }

        const debounceTimer = setTimeout(async () => {
            const appliedValues = lastAppliedFilterValuesRef.current

            if (!appliedValues) return

            setLoadingStatus("tableDataLoading")

            try {
                await fetchSecurityExclusionAuditData({
                    selectedYear: appliedValues?.year ?? "",
                    selectedQuarter: appliedValues?.quarter ?? "",
                    selectedFamilyId: appliedValues?.family?.family_id ?? "",
                    selectedCurrentPage: 1,
                    selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                    searchInput: searchText,
                    selectedClientCode: appliedValues?.individual?.clientCode ?? "",
                    cancelledRef
                })
            } catch (error) {
                if (cancelled) return
                console.log(error)
            } finally {
                if (!cancelled) {
                    setLoadingStatus("")
                }
            }
        }, DEBOUNCE_DELAY)

        return () => {
            clearTimeout(debounceTimer)
            cancelled = true
            cancelledRef.current = true
        }
    }, [searchText])

    return (
        <>
            <Backdrop
                sx={(theme: Theme) => ({
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.modal + 1
                })}
                open={["individualDataLoading"].includes(loadingStatus)}
            >
                <CircularProgress sx={(theme: Theme) => ({ color: theme.palette.primary[500] })} />
            </Backdrop>
            <Box sx={{ marginBottom: "0.5rem" }}>
                <BreadCrum array={routerPath} />
            </Box>
            <Typography
                variant="h1"
                sx={{
                    color: theme.palette.text.primary,
                    flexShrink: 0,
                    marginTop: "10px",
                    marginBottom: "2rem",
                }}
            >
                Audit History
            </Typography>
            <Box
                sx={{
                    width: { xs: "100%", lg: "auto" },
                    minWidth: 0,
                    maxWidth: "100%"
                }}
            >
                <Formik
                    innerRef={filterFormikRef}
                    validationSchema={filterValidationSchema}
                    initialValues={initialFilterValues}
                    onSubmit={handleApplyFilters}
                >
                    {({ values }) => {
                        const quarterOptions = getQuartersForFiscalYear(values?.year ?? "")

                        return (
                            <Form style={{ width: "100%", maxWidth: "100%" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", lg: "row" },
                                        flexWrap: { xs: "nowrap", lg: "wrap" },
                                        alignItems: { xs: "stretch", lg: "flex-start" },
                                        justifyContent: 'flex-start',
                                        gap: { xs: "0.8rem", lg: "1rem" },
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    <Box sx={filterFieldWrapperSx}>
                                        <MFSelectField
                                            name='year'
                                            label="Year *"
                                            items={YEARS}
                                            onChange={handleYearChange}
                                            disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterQuarterWrapperSx}>
                                        <MFSelectField
                                            name='quarter'
                                            label="Quarter *"
                                            items={quarterOptions}
                                            onChange={handleQuarterChange}
                                            disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSearchDropdownWrapperSx}>
                                        <FormikSearchDropdown
                                            name="family"
                                            label="Family *"
                                            options={familiesList ?? []}
                                            fetchData={fetchFamiliesData}
                                            filterOptions={(x) => x}
                                            onOpen={() => {
                                                if (!filterFormikRef?.current?.values?.family) {
                                                    setFamiliesList([]);
                                                }
                                            }}
                                            onChange={handleFamilyChange}
                                            disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSearchDropdownWrapperSx}>
                                        <FormikSearchDropdown
                                            name="individual"
                                            label="Individual *"
                                            options={values?.individualArr ?? []}
                                            disabled={["appliedFilterLoading", "tableDataLoading"].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSubmitWrapperSx}>
                                        <MFSubmitButton
                                            label="Apply Filters"
                                            fullWidth={false}
                                            sx={filterSubmitButtonSx(theme)}
                                            loadingEnable={["appliedFilterLoading"].includes(loadingStatus)}
                                            disabled={["tableDataLoading"].includes(loadingStatus)}
                                            loadingPosition="center"
                                        />
                                    </Box>
                                </Box>
                            </Form>
                        )
                    }}
                </Formik>
            </Box>
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginTop: "1.5rem"
                }}
            >
                {!areFiltersApplied && <NoData />}
                {areFiltersApplied && (
                    <>
                        <TextField
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="Clear search"
                                            sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                            onMouseDown={handleClearSearch}
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: '10px',
                                    height: '50px',
                                    border: `1px solid ${theme.palette.text.secondary}B2`,
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '& input': {
                                        paddingLeft: '10px',
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            ...theme.typography.subtitle_400,
                                            color: theme.palette.text.secondary,
                                            opacity: 1,
                                        },
                                    },
                                    marginBottom: "3rem"
                                },
                            }}
                            value={searchText}
                            placeholder="Search..."
                            onChange={handleSearchTextChange}
                        />
                        <AuditHistoryTable
                            isLoading={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                            tableData={tableData}
                            tablePaginationObj={tablePaginationObj}
                            handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                            handleTablePageChange={handleTablePageChange}
                            handleViewDetailsClick={handleViewDetailsClick}
                            loadingRowId={loadingRowId}
                        />
                    </>
                )}
            </Box>
            <AuditDetailsPopup
                open={auditDetailsPopupData.isOpen}
                onClose={handleAuditDetailsPopupClose}
                data={auditDetailsPopupData.data}
                comingFrom="auditHistory"
            />
        </>
    )
}